import HeartOutline from '@monofront/design-system/visuals/Icons/Old/HeartOutline';
import HeartFilled from '@monofront/design-system/visuals/Icons/Old/HeartFilled';
import List from '@monofront/design-system/visuals/Icons/Old/List';
import AddButtonWithLabel from '@monofront/design-system/components/AddButtonWithLabel';
import Checkbox from '@monofront/design-system/components/Form/Checkbox';
import Label from '@monofront/design-system/components/Form/Label';
import { redirectToLogin } from '@utils/redirect';
import { useAuthentication } from '@context/businessContainer';
import { useWishlist } from '@context/businessContainer';
import Popover from '@components/Popover';
import { useRouter } from 'next/router';
import {
  IProduct,
  IWishlist,
  ISearchProduct,
  IShoppingCartProduct,
} from '@monofront/types';
import CreateWishlistModal from '@components/CreateWishlistModal';
import { fireAutoMLEvent, dataLayerTrackEvent } from '@utils/tracking';
import { trackEvent } from '@monofront/tracking';
import { zindex } from '@monofront/theme';
import { useTranslations } from 'next-intl';
import useI18n from '@hooks/i18n/useI18n';
import { getProductCategory } from '@monofront/business/utils';

interface IButtonAddToWishlistProps {
  variantId: string;
  name: string;
  sku: string;
  comparedPrice?: number;
  price: number;
  large?: boolean;
  hasButtonLook?: boolean;
  image?: string;
  product: ISearchProduct | IShoppingCartProduct;
  UNSAFE_className?: string;
}

const ButtonAddToWishlist: React.FC<IButtonAddToWishlistProps> = ({
  variantId,
  name,
  sku,
  comparedPrice,
  price,
  large,
  hasButtonLook = false,
  product,
  UNSAFE_className = '',
}) => {
  const { isAuthenticated } = useAuthentication();
  const {
    wishlists,
    addProductToWishlist,
    deleteProductFromWishlist,
    isProductInOneWishlist,
  } = useWishlist();
  const router = useRouter();
  const widthHeart = large ? 32 : 24;
  const heightHeart = large ? 32 : 24;
  const { currency } = useI18n();

  const handleAddToWishlist = async (wishlistUid: string) => {
    if (!isAuthenticated) {
      redirectToLogin(router.asPath);

      return;
    }

    await addProductToWishlist(wishlistUid, variantId);

    // deprecated tracker https://gitlab.com/la-fourche/scrum/-/issues/1968
    fireAutoMLEvent('add-to-list', {
      productEventDetail: {
        productDetails: [
          {
            id: `${sku}`,
            currencyCode: 'EUR',
            originalPrice: comparedPrice,
            displayPrice: price,
          },
        ],
        listId: wishlistUid,
      },
    });

    dataLayerTrackEvent('productAddedToWishlist', {
      productName: name,
    });

    trackEvent({
      type: 'LFAddToWishlist',
      data: {
        products: [
          {
            name: product.title,
            sku: product.sku,
            price: price * 100,
            brand: product.vendor,
            category: getProductCategory(product) ?? undefined,
            quantity: 1,
          },
        ],
        currency,
      },
    });
  };

  const handleDeleteFromWishlist = async (wishlistUid: string) => {
    if (!isAuthenticated) {
      redirectToLogin(router.asPath);

      return;
    }

    await deleteProductFromWishlist(wishlistUid, variantId);

    fireAutoMLEvent('remove-from-list', {
      productEventDetail: {
        productDetails: [
          {
            id: sku,
            currencyCode: 'EUR',
            originalPrice: comparedPrice,
            displayPrice: price,
          },
        ],
        listId: wishlistUid,
      },
    });
  };

  const tWishlists = useTranslations('Wishlists');

  return (
    <>
      <div className={`container ${UNSAFE_className}`}>
        <Popover
          trigger="click"
          isLight
          interactive
          zIndex={zindex.modalBackdrop - 1} // to allow the NewList modal to pass over
          appendTo={global?.document?.body}
          placement="bottom-start"
          content={
            <div className="list">
              <div className="title-modal">
                <List />
                <p>{tWishlists('addToWishlist')}</p>
              </div>
              <ul>
                {wishlists.map((wishlist: IWishlist) => {
                  const isProductInWishlist = wishlist.products.some(
                    ({ objectID }: IProduct) => objectID === variantId,
                  );

                  return (
                    <li key={wishlist.id}>
                      <div
                        onClick={() => handleDeleteFromWishlist(wishlist.id)}
                        className="list-item"
                      >
                        <Label id={wishlist.id}>
                          {wishlist.defaultWishlist
                            ? `${tWishlists('defaultWishlistName')} 💛`
                            : wishlist.name}
                        </Label>
                        <Checkbox
                          name="wishlist"
                          id={wishlist.id}
                          required={false}
                          defaultChecked={isProductInWishlist}
                          onChange={() => {
                            return !isProductInWishlist
                              ? handleAddToWishlist(wishlist.id)
                              : handleDeleteFromWishlist(wishlist.id);
                          }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
              <div className="separator" />
              <CreateWishlistModal
                triggerButtonRender={(setIsCreateWishlistModalOpen) => (
                  <div
                    className="add-list"
                    onClick={() => {
                      if (!isAuthenticated) {
                        redirectToLogin(router.asPath);

                        return;
                      }

                      setIsCreateWishlistModalOpen(true);
                    }}
                  >
                    <AddButtonWithLabel handleClick={() => undefined}>
                      {tWishlists('createList')}
                    </AddButtonWithLabel>
                  </div>
                )}
              />
            </div>
          }
        >
          <div
            className={
              hasButtonLook
                ? 'tw-lf-button-icon tw-lf-button-icon-lg tw-lf-button-icon-primary'
                : 'wish'
            }
          >
            {isProductInOneWishlist(variantId) ? (
              <HeartFilled width={widthHeart} height={heightHeart} />
            ) : (
              <HeartOutline width={widthHeart} height={heightHeart} />
            )}
          </div>
        </Popover>
      </div>
      <style jsx>
        {`
          .container {
            width: ${hasButtonLook ? 'auto' : '35px'};
            height: ${hasButtonLook ? 'auto' : '35px'};
          }

          .wish {
            cursor: pointer;
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100%;
          }

          .wish :global(svg > path) {
            fill: var(--black);
          }

          .title-modal {
            display: flex;
            justify-content: center;
            align-items: center;
            flex-direction: column;
          }

          .title-modal > p {
            margin: var(--space-2) 0 var(--space-3) 0;
            font-weight: var(--font-weight-base);
          }

          .separator {
            border: var(--border);
            margin: var(--space-2) 0;
          }

          .list {
            display: flex;
            flex-direction: column;
            padding: var(--space-5);
            min-width: 270px;
            font-size: var(--font-size-2);
          }

          .list > ul > li {
            margin-bottom: var(--space-2);
            text-align: left;
          }

          .list > ul > li,
          .add-list {
            cursor: pointer;
          }

          .add-list {
            display: flex;
            align-items: center;
          }

          .add-list > span {
            margin-left: var(--space-3);
          }

          .list-item {
            display: flex;
            justify-content: space-between;
          }
        `}
      </style>
    </>
  );
};

export default ButtonAddToWishlist;
